/**
 * Shared validation primitives.
 *
 * Split out from `index.ts` so that the per-module schema files can import
 * them WITHOUT importing the barrel that re-exports those same modules. A
 * cycle through the barrel would work by accident in ESM and break in the
 * CommonJS build, where a partially evaluated module hands back undefined for
 * a schema another schema is being built from.
 */
import { z } from 'zod';
import { ALL_LOCALES, PAGINATION_DEFAULTS } from '@lims/shared-types';
import { ALL_ROLES } from '@lims/permissions';
// ---------------------------------------------------------------------------
// Primitives
// ---------------------------------------------------------------------------
export const uuidSchema = z.string().uuid();
export const localeSchema = z.enum(ALL_LOCALES);
export const roleSchema = z.enum(ALL_ROLES);
/**
 * Trimmed, non-empty text with a length bound.
 * Trimming first means a field of only whitespace fails as empty rather than
 * being stored as blank content.
 */
export const boundedText = (min, max) => z.string().trim().min(min).max(max);
/**
 * ONE business value, typed once, in any language or mix of languages (D183).
 *
 * The interface is bilingual; the laboratory's business data is not. A
 * client's name, a project's contractor or a department is one fact, and a
 * person writes it once in whatever language they use. Nothing is translated.
 */
export const businessTextSchema = boundedText(1, 500);
/**
 * An optional business value. An empty string and `null` both mean "none" and
 * reach the server as `null` — never as an empty object that a stricter rule
 * then refuses. That mismatch is what refused every project saved without a
 * contractor, consultant, owner or description (`POST /projects` returned
 * 400: the form sent `{ ar: null, en: null }` for each empty pair).
 */
export const optionalBusinessText = (max = 500) => z
    .string()
    .trim()
    .max(max)
    .nullable()
    .optional()
    .transform((value) => (value === '' ? null : value));
/**
 * Whether a value holds a character no business reference may hold: a C0 or
 * C1 control character (a line break, a tab, a NUL) or a Unicode direction
 * override, embedding or isolate. The last group can make a printed number
 * READ in a different order from the one stored — a spoofing risk on payment
 * details. The left-to-right and right-to-left MARKS stay allowed: they
 * reorder nothing.
 */
export function hasUnsafeReferenceCharacter(value) {
    for (const character of value) {
        const code = character.codePointAt(0) ?? 0;
        if (code <= 0x1f ||
            (code >= 0x7f && code <= 0x9f) ||
            (code >= 0x202a && code <= 0x202e) ||
            (code >= 0x2066 && code <= 0x2069)) {
            return true;
        }
    }
    return false;
}
/**
 * A business reference the laboratory owns (D206): an IBAN, a SWIFT / BIC
 * code, an account number, a VAT or commercial registration number.
 *
 * Stored EXACTLY as written — only the spaces around it are trimmed; nothing
 * is compacted, upper-cased or "corrected". The laboratory is responsible for
 * its own references and the product does not certify them as genuine, so
 * there is no checksum, no national length and no character set: a
 * multi-tenant product must not refuse a value a laboratory considers
 * authoritative because one country's validator disagrees. What remains are
 * the generic safety limits every text has: a length and the characters above.
 */
export const referenceTextSchema = (max) => z
    .string()
    .trim()
    .max(max)
    .refine((value) => !hasUnsafeReferenceCharacter(value), 'Contains a control or text-direction character');
/**
 * Bilingual content. At least one language must be provided, so a record can
 * never be created with no readable name in either language.
 *
 * Used only where BOTH languages are a real requirement (D187): catalogue
 * names and specifications printed per report language, and the laboratory's
 * own identity and document wording. Business data uses `businessTextSchema`.
 */
export const bilingualTextSchema = z
    .object({
    ar: z.string().trim().max(500).nullable().optional(),
    en: z.string().trim().max(500).nullable().optional(),
})
    .refine((value) => Boolean(value.ar?.length) || Boolean(value.en?.length), { message: 'At least one language value is required' });
/**
 * Money as integer minor units. Rejects decimals outright — a fractional minor
 * unit indicates the caller is using floating-point arithmetic somewhere.
 */
export const moneyMinorSchema = z
    .number()
    .int({ message: 'Amount must be an integer number of minor units' })
    .nonnegative();
export const currencySchema = z
    .string()
    .trim()
    .length(3)
    .regex(/^[A-Z]{3}$/, 'Currency must be an ISO 4217 code');
export const moneySchema = z.object({
    amountMinor: moneyMinorSchema,
    currency: currencySchema,
});
/** Email, normalised to lowercase so duplicate detection is reliable. */
export const emailSchema = z
    .string()
    .trim()
    .toLowerCase()
    .email()
    .max(320);
/** Permissive international phone format — laboratories serve many countries. */
export const phoneSchema = z
    .string()
    .trim()
    .min(5)
    .max(32)
    .regex(/^[+]?[0-9\s()-]+$/, 'Invalid phone number format');
/** ISO 8601 timestamp. All timestamps are stored and transmitted as UTC. */
export const isoTimestampSchema = z.string().datetime({ offset: true });
const ISO_DATE_SHAPE = /^\d{4}-\d{2}-\d{2}$/;
/**
 * A calendar date, `YYYY-MM-DD`. The shape alone is not enough: `2027-02-30`
 * matches it, and an impossible day that reaches the database's `::date` cast
 * fails there as a `500` instead of a validation refusal. A day that does not
 * exist is refused here, with zod's own `invalid_date` code, once for every
 * route that uses this schema.
 */
export const isoDateSchema = z
    .string()
    .regex(ISO_DATE_SHAPE, 'Expected an ISO date (YYYY-MM-DD)')
    .superRefine((value, ctx) => {
    // A value of the wrong shape was already refused above; reporting it again
    // would say the same thing under a second code.
    if (ISO_DATE_SHAPE.test(value) && !isCalendarDate(value)) {
        ctx.addIssue({
            code: z.ZodIssueCode.invalid_date,
            message: 'The date does not exist in the calendar',
        });
    }
});
/**
 * Whether a `YYYY-MM-DD` string names a day that exists. Year 0000 is refused
 * because the database has no year zero.
 */
function isCalendarDate(value) {
    const year = Number(value.slice(0, 4));
    const month = Number(value.slice(5, 7));
    const day = Number(value.slice(8, 10));
    if (year < 1 || month < 1 || month > 12 || day < 1)
        return false;
    const leap = (year % 4 === 0 && year % 100 !== 0) || year % 400 === 0;
    const daysInMonth = [31, leap ? 29 : 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];
    return day <= daysInMonth[month - 1];
}
/**
 * A reason string required on actions that must be explainable: returning a
 * result, cancelling a record, overriding a price, or using out-of-calibration
 * equipment. Deliberately requires substance, not a single character.
 */
export const reasonSchema = boundedText(3, 2000);
/**
 * The password policy, as named rules.
 *
 * Declared ONCE and used twice: `newPasswordSchema` below is built from it,
 * and the interface lists the same rules as a live checklist beside the field
 * (`passwordPolicyFailures`), so what the form tells a person to type and what
 * the server accepts cannot drift apart. The rule NAMES are the stable part —
 * each is a translation key in the interface, never English prose from here.
 */
export const PASSWORD_MIN_LENGTH = 12;
export const PASSWORD_MAX_LENGTH = 256;
export const PASSWORD_RULES = ['length', 'lowercase', 'uppercase', 'digit'];
const PASSWORD_RULE_TESTS = {
    length: (value) => value.length >= PASSWORD_MIN_LENGTH,
    lowercase: (value) => /[a-z]/.test(value),
    uppercase: (value) => /[A-Z]/.test(value),
    digit: (value) => /[0-9]/.test(value),
};
/** The rules a candidate password does NOT yet meet, in policy order. */
export function passwordPolicyFailures(value) {
    return PASSWORD_RULES.filter((rule) => !PASSWORD_RULE_TESTS[rule](value));
}
/**
 * Password policy for newly set passwords — staff and portal alike, and
 * whoever sets them: the person themselves, or a laboratory manager creating
 * or resetting an account. Minimum 12 characters with mixed character classes.
 */
export const newPasswordSchema = z
    .string()
    .min(PASSWORD_MIN_LENGTH, 'Password must be at least 12 characters')
    .max(PASSWORD_MAX_LENGTH)
    .regex(/[a-z]/, 'Password must contain a lowercase letter')
    .regex(/[A-Z]/, 'Password must contain an uppercase letter')
    .regex(/[0-9]/, 'Password must contain a digit');
// ---------------------------------------------------------------------------
// Pagination
// ---------------------------------------------------------------------------
export const listQuerySchema = z.object({
    page: z.coerce.number().int().positive().default(PAGINATION_DEFAULTS.PAGE),
    pageSize: z.coerce
        .number()
        .int()
        .positive()
        .max(PAGINATION_DEFAULTS.MAX_PAGE_SIZE)
        .default(PAGINATION_DEFAULTS.PAGE_SIZE),
    sort: z.string().trim().max(64).optional(),
    order: z.enum(['asc', 'desc']).default('desc'),
    search: z.string().trim().max(200).optional(),
});
export { z };
//# sourceMappingURL=primitives.js.map